import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { faArrowLeft, faEdit, faInfoCircle, faExchangeAlt } from '@fortawesome/free-solid-svg-icons';
import { NgbOffcanvas, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { BalanceAccountService } from 'src/app/share/services/balance-account/balance-account.service';
import { PaymentAccountsService } from 'src/app/share/services/payment-account/payment-accounts.service';
import { BalanceAccountDetailComponent } from '../balance-account-detail/balance-account-detail.component';
import { NewBalanceTransferModalComponent } from '../new-balance-transfer-modal/new-balance-transfer-modal.component';

@Component({
  selector: 'app-balance-transfers',
  templateUrl: './balance-transfers.component.html',
  styleUrls: ['./balance-transfers.component.scss'],
  standalone: false
})
export class BalanceTransfersComponent implements OnInit, OnDestroy {
  protected readonly faArrowLeft = faArrowLeft;
  protected readonly faEdit = faEdit;
  protected readonly faInfoCircle = faInfoCircle;
  protected readonly faExchangeAlt = faExchangeAlt;

  balanceAccountId: string = '';
  accountDetails: any = null;
  accountSubscription!: Subscription;

  summaryStats: any = null;

  // Balance Transfers List
  transfersList: any[] = [];
  transfersLoader: boolean = false;
  transfersErrorMessage: string = '';
  transfersPage: number = 0;
  endOfTransfers: boolean = false;

  // Linked Payment Accounts List
  paymentAccounts: any[] = [];
  paymentAccountsLoader: boolean = false;
  paymentAccountsErrorMessage: string = '';
  paymentAccountsPage: number = 0;
  endOfPaymentAccounts: boolean = false;

  colors: string[] = [
    '#0369A1',
    '#A16207',
    '#7C3AED',
    '#9D174D',
    '#047857',
    '#4F46E5',
    '#0D9488',
    '#DC2626',
    '#2563EB',
    '#EA580C'
  ];

  constructor(
    private readonly route: ActivatedRoute,
    private readonly balanceAccountService: BalanceAccountService,
    private readonly paymentAccountsService: PaymentAccountsService,
    private readonly offcanvasService: NgbOffcanvas,
    private readonly modalService: NgbModal
  ) {}

  ngOnInit(): void {
    this.balanceAccountId = this.route.snapshot.paramMap.get('id') || '';

    this.accountSubscription = this.balanceAccountService.selectedAccount$.subscribe((acc: any) => {
      if (acc && acc._id === this.balanceAccountId) {
        this.accountDetails = acc;
      }
    });

    if (this.balanceAccountId) {
      this.loadSummaryStats();
      this.loadTransfersList(true);
      this.loadLinkedPaymentAccounts(true);
    }
  }

  ngOnDestroy(): void {
    if (this.accountSubscription) {
      this.accountSubscription.unsubscribe();
    }
  }

  loadSummaryStats(): void {
    this.balanceAccountService.getBalanceTransferSummary(this.balanceAccountId).subscribe({
      next: (res: any) => {
        this.summaryStats = res?.data || res || {};
        if (this.summaryStats) {
          this.accountDetails = {
            _id: this.balanceAccountId,
            accountName: this.summaryStats.accountName,
            accountNumber: this.summaryStats.accountNumber,
            amount: this.summaryStats.balance,
            status: 'Active'
          };
        }
      },
    });
  }

  loadTransfersList(reset: boolean = false): void {
    if (reset) {
      this.transfersPage = 0;
      this.transfersList = [];
      this.endOfTransfers = false;
    }

    this.transfersLoader = true;
    this.transfersErrorMessage = '';

    const payload = {
      balanceAccountId: this.balanceAccountId,
      page: this.transfersPage
    };

    this.balanceAccountService.getBalanceTransfers(payload).subscribe({
      next: (res: any) => {
        this.transfersLoader = false;
        const data = res?.data || [];
        this.transfersList = this.transfersPage === 0 ? data : [...this.transfersList, ...data];
        if (data.length === 0) {
          this.endOfTransfers = true;
        }
      },
      error: (err: any) => {
        this.transfersLoader = false;
        this.transfersErrorMessage = err?.error?.error?.message || err?.error?.message || 'Failed to load balance transfers';
      }
    });
  }

  onScrollTransfers(): void {
    if (this.transfersLoader || this.endOfTransfers) return;
    this.transfersPage++;
    this.loadTransfersList();
  }

  loadLinkedPaymentAccounts(reset: boolean = false): void {
    if (reset) {
      this.paymentAccountsPage = 0;
      this.paymentAccounts = [];
      this.endOfPaymentAccounts = false;
    }

    this.paymentAccountsLoader = true;
    this.paymentAccountsErrorMessage = '';

    const payload = {
      balanceAccountId: this.balanceAccountId,
      page: this.paymentAccountsPage
    };

    this.paymentAccountsService.getPaymentAccountList(payload).subscribe({
      next: (res: any) => {
        this.paymentAccountsLoader = false;
        const data = res?.data || [];
        this.paymentAccounts = this.paymentAccountsPage === 0 ? data : [...this.paymentAccounts, ...data];
        if (data.length === 0) {
          this.endOfPaymentAccounts = true;
        }
      },
      error: (err: any) => {
        this.paymentAccountsLoader = false;
        this.paymentAccountsErrorMessage = err?.error?.error?.message || err?.error?.message || 'Failed to load linked payment accounts';
      }
    });
  }

  onScrollPaymentAccounts(): void {
    if (this.paymentAccountsLoader || this.endOfPaymentAccounts) return;
    this.paymentAccountsPage++;
    this.loadLinkedPaymentAccounts();
  }

  openEditAccountModal(): void {
    const offcanvasRef = this.offcanvasService.open(BalanceAccountDetailComponent, {
      position: 'end',
      panelClass: 'balance-account-detail-offcanvas',
      backdrop: 'static',
      keyboard: false
    });

    offcanvasRef.componentInstance.mode = 'EDIT';
    offcanvasRef.componentInstance.accountData = this.accountDetails;
    offcanvasRef.result.then((result) => {
      if (result === 'SAVED') {
        this.loadSummaryStats();
      }
    }, () => {});
  }

  openNewTransferModal(): void {
    const modalRef = this.modalService.open(NewBalanceTransferModalComponent, {
      centered: true,
      backdrop: 'static',
      keyboard: false,
      windowClass: 'new-transfer-modal-wrapper'
    });

    modalRef.componentInstance.defaultSourceAccountId = this.balanceAccountId;
    modalRef.result.then((result) => {
      if (result === 'SAVED') {
        this.loadSummaryStats();
        this.loadTransfersList(true);
      }
    }, () => {});
  }

  formatReference(ref: string): string {
    if (!ref) return '-';
    const trimmedRef = String(ref).trim();
    if (trimmedRef.startsWith('xxx') || trimmedRef.startsWith('***')) return trimmedRef;
    if (trimmedRef.length > 4) {
      return '***' + trimmedRef.slice(-4);
    }
    return '***' + trimmedRef;
  }

  getBadgeColor(id: string, index: number): string {
    const charCodeSum = id ? id.split('').reduce((sum: number, char: string) => sum + (char.codePointAt(0) ?? 0), 0) : index;
    const colorIndex = charCodeSum % this.colors.length;
    return this.colors[colorIndex];
  }
}
